import { createSlice } from '@reduxjs/toolkit';
import axiosInstance from 'src/utils/axiosInstance';

const initialState = {
  roles: [],
  totalCount: 0,
  totalPages: 0,
  loading: false,
  error: null,
};

export const roleSlice = createSlice({
  name: 'role',
  initialState,
  reducers: {
    setRoles: (state, action) => {
      state.roles = action.payload;
    },
    setTotalCount: (state, action) => {
      state.totalCount = action.payload;
    },
    setTotalPages: (state, action) => {
      state.totalPages = action.payload;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
  },
});

export const { setRoles, setTotalCount, setTotalPages, setLoading, setError } = roleSlice.actions;

// Get roles (paginated & searchable)
export const getRoles = (filters) => async (dispatch) => {
  dispatch(setLoading(true));
  dispatch(setError(null));
  try {
    const page = filters.page || 1;
    const limit = filters.limit || 10;
    const search = filters.search || '';
    const response = await axiosInstance.get('/admin/role/list', {
      params: { page, limit, search }
    });
    if (response.data && response.data.status === true) {
      const result = response.data.data.result || [];
      const totalCount = response.data.data.totalCount || result.length;
      const totalPages = response.data.data.totalPages || 1;
      dispatch(setRoles(result));
      dispatch(setTotalCount(totalCount));
      dispatch(setTotalPages(totalPages));
      return { status: true, data: result, totalCount, totalPages };
    } else {
      dispatch(setRoles([]));
      dispatch(setTotalCount(0));
      dispatch(setTotalPages(0));
      const errorMsg = response.data?.message || 'Failed to fetch roles';
      dispatch(setError(errorMsg));
      return { status: false, message: errorMsg };
    }
  } catch (error) {
    dispatch(setRoles([]));
    dispatch(setTotalCount(0));
    dispatch(setTotalPages(0));
    const errorMsg = error.response?.data?.message || error.message || 'Failed to fetch roles';
    dispatch(setError(errorMsg));
    return { status: false, message: errorMsg };
  } finally {
    dispatch(setLoading(false));
  }
};

// Get single role details (prefill edit form)
export const getRoleDetails = (id) => async () => {
  try {
    const response = await axiosInstance.get(`/admin/role/getbyId/${id}`);
    if (response.data && response.data.status === true) {
      return { status: true, data: response.data.data };
    } else {
      return { status: false, message: response.data?.message || 'Failed to fetch role details' };
    }
  } catch (error) {
    const errorMsg = error.response?.data?.message || error.message || 'Failed to fetch role details';
    return { status: false, message: errorMsg };
  }
};

// Add new role
export const addRole = (payload) => async () => {
  try {
    const response = await axiosInstance.post('/admin/role/add', payload);
    if (response.data && response.data.status === true) {
      return { status: true, data: response.data.data, message: response.data?.message };
    } else {
      return { status: false, message: response.data?.message || 'Failed to add role' };
    }
  } catch (error) {
    const errorMsg = error.response?.data?.message || error.message || 'Failed to add role';
    return { status: false, message: errorMsg };
  }
};

// Update role details
export const updateRole = (id, payload) => async () => {
  try {
    const response = await axiosInstance.put(`/admin/role/update/${id}`, payload);
    if (response.data && response.data.status === true) {
      return { status: true, data: response.data.data, message: response.data?.message };
    } else {
      return { status: false, message: response.data?.message || 'Failed to update role' };
    }
  } catch (error) {
    const errorMsg = error.response?.data?.message || error.message || 'Failed to update role';
    return { status: false, message: errorMsg };
  }
};

// Delete role
export const deleteRole = (id) => async () => {
  try {
    const response = await axiosInstance.delete(`/admin/role/delete/${id}`);
    if (response.data && response.data.status === true) {
      return { status: true, message: response.data?.message };
    } else {
      return { status: false, message: response.data?.message || 'Failed to delete role' };
    }
  } catch (error) {
    const errorMsg = error.response?.data?.message || error.message || 'Failed to delete role';
    return { status: false, message: errorMsg };
  }
};

export default roleSlice.reducer;
